import type { AuditableEntity } from "../types";

/**
 * Mirrors `backend/src/services/sessions/entities/number-requests.ts`.
 */

export type NumberRequestKind = "new_number" | "port_existing" | "sip_trunk";

/**
 * What a company chooses between when it opens a request. Keeping an existing
 * number is no longer offered (old rows still carry `port_existing`).
 */
export const NUMBER_REQUEST_CREATION_KINDS: NumberRequestKind[] = [
  "new_number",
  "sip_trunk",
];

/** Who the line is for — see the backend entity. Empty on an older request. */
export type NumberRequestHolderType = "" | "company" | "individual";

/** How the company was established — see the backend entity. */
export type NumberRequestCompanyCheck =
  | ""
  | "verified"
  | "unverified"
  | "foreign";

/**
 * Opened through the guided creation: everything was asked in the dialog, so
 * the page only shows where the request stands.
 */
export const isGuidedNumberRequest = (request: NumberRequest) =>
  Boolean(request.holder_type);

/** The SIREN lookup's answer. `invalid` / `not_found` are the refusals. */
export interface CompanyLookup {
  siren: string;
  name: string;
  address: string;
  postcode: string;
  city: string;
  check: "verified" | "unverified" | "invalid" | "not_found";
}

export type AvailableNumberType = "local" | "mobile";

export interface AvailableNumber {
  phone_number: string;
  friendly_name: string;
  locality: string;
}

export interface AvailableNumbers {
  /** The prefix searched — the server's suggestion when none was given. */
  prefix: string;
  numbers: AvailableNumber[];
  /** False when the platform has no carrier account to search. */
  searchable: boolean;
}

/** Offered in this order — simplest first. */
export const NUMBER_REQUEST_KINDS: NumberRequestKind[] = [
  "port_existing",
  "new_number",
  "sip_trunk",
];

/**
 * `sip_trunk` is handled by e-mail, not by the client filling in a form: there
 * is nothing to upload and nothing to fill in beyond describing their setup.
 * The UI hides the checklist and the number fields for it and shows what
 * happens next instead.
 */
export const isEmailHandledKind = (kind: NumberRequestKind) =>
  kind === "sip_trunk";

/** @deprecated Nothing is ported any more — see the backend entity. */
export type NumberRequestInternalLine = "" | "new_contract" | "heyup_phone";

/** The handsets we ship pre-configured. Offered in this order. */
export type NumberRequestHandset = "wired_phone" | "wireless_phone";

export const NUMBER_REQUEST_HANDSETS: NumberRequestHandset[] = [
  "wired_phone",
  "wireless_phone",
];

/**
 * What the client will answer the transferred calls on: how many of each
 * handset, plus the way out for anyone who already has a VoIP/SIP phone.
 *
 * Ordering nothing is a valid answer, and the common one: with no handset the
 * transfers land on the mobile number configured on the account.
 */
export interface NumberRequestHardwareOrder {
  quantities: Partial<Record<NumberRequestHandset, number>>;
  own_device: boolean;
}

export const emptyHardwareOrder = (): NumberRequestHardwareOrder => ({
  quantities: {},
  own_device: false,
});

/** Unit price in euros of either handset. Mirrors the backend constant. */
export const NUMBER_REQUEST_HANDSET_UNIT_PRICE_EUR = 75;

/** Past this it is a quote rather than a self-service order. */
export const NUMBER_REQUEST_HANDSET_MAX_QUANTITY = 20;

/** Product shots, served from `public/hardware/`. */
export const NUMBER_REQUEST_HANDSET_IMAGES: Record<
  NumberRequestHandset,
  string
> = {
  wired_phone: "/hardware/phone-wired.jpg",
  wireless_phone: "/hardware/phone-wireless.jpg",
};

export const handsetsOrdered = (order?: NumberRequestHardwareOrder) =>
  NUMBER_REQUEST_HANDSETS.reduce(
    (total, handset) => total + (order?.quantities?.[handset] || 0),
    0,
  );

/** What the order comes to, in euros. */
export const hardwareOrderTotal = (order?: NumberRequestHardwareOrder) =>
  handsetsOrdered(order) * NUMBER_REQUEST_HANDSET_UNIT_PRICE_EUR;

/** Whether the client has answered the hardware question at all. */
export const hasHardwareOrder = (order?: NumberRequestHardwareOrder) =>
  handsetsOrdered(order) > 0 || Boolean(order?.own_device);

/**
 * The optional blocks of the detail form, by name.
 *
 * Each of them is legitimately answerable with nothing at all, so their content
 * cannot tell "left empty on purpose" from "not looked at yet". The request
 * carries the list of the ones the client has saved (`answered`), and that is
 * what decides whether a block still asks its question.
 */
export type NumberRequestBlock =
  | "current_number"
  | "desired_prefix"
  | "hardware"
  | "notes"
  | "notification_email";

export const isBlockAnswered = (
  request: NumberRequest,
  block: NumberRequestBlock,
) => (request.answered?.items || []).includes(block);

export const NUMBER_REQUEST_STATES = [
  "draft",
  "documents_pending",
  "documents_review",
  "submitted",
  "approved",
  "provisioned",
  "rejected",
  "cancelled",
] as const;

export type NumberRequestState = (typeof NUMBER_REQUEST_STATES)[number];

/**
 * The happy path, in order — what the progress indicator walks through.
 * `rejected` / `cancelled` are exits, not steps, so they are not in here.
 */
export const NUMBER_REQUEST_STEPS: NumberRequestState[] = [
  "draft",
  "documents_pending",
  "documents_review",
  "submitted",
  "approved",
  "provisioned",
];

/**
 * States where the request is blocked on the client: nothing submitted yet
 * (`draft`) or supporting documents still missing (`documents_pending`).
 * These are what the sidebar badge on "Canaux" counts.
 */
export const NUMBER_REQUEST_STATES_NEEDING_CLIENT: NumberRequestState[] = [
  "draft",
  "documents_pending",
];

export const NUMBER_REQUEST_TERMINAL_STATES: NumberRequestState[] = [
  "provisioned",
  "rejected",
  "cancelled",
];

/**
 * A validated request: the number is live and the file is closed.
 *
 * Nothing on it is the client's to change any more — the papers behind a
 * working line are the carrier file we filed, not their working copy — so the
 * detail page drops every editable affordance, the checklist is not shown, and
 * the row cannot be deleted (the backend refuses it too, see
 * `sessions/services/number-requests.ts`). `rejected` and `cancelled` are not
 * in here: those are closed *without* a line, and deleting one is how a client
 * tidies up after changing their mind.
 */
export const isNumberRequestValidated = (request: NumberRequest) =>
  request.state === "provisioned";

/** Must match the backend's table — it is the backend that enforces it. */
export const NUMBER_REQUEST_TRANSITIONS: Record<
  NumberRequestState,
  NumberRequestState[]
> = {
  draft: ["documents_pending", "documents_review", "cancelled"],
  documents_pending: ["documents_review", "cancelled"],
  documents_review: ["submitted", "documents_pending", "rejected", "cancelled"],
  submitted: ["approved", "rejected", "cancelled"],
  approved: ["provisioned", "rejected", "cancelled"],
  provisioned: [],
  rejected: [],
  cancelled: [],
};

export interface NumberRequestDocument {
  type: string;
  label: string;
  provided: boolean;
  file_key?: string;
  filename?: string;
  content_type?: string;
  size?: number;
  uploaded_at?: number;
}

export interface NumberRequestHistoryEntry {
  state: NumberRequestState;
  at: number;
  by: string;
  comment?: string;
}

/** A parcel shipped to the client, with its tracking link. */
export interface NumberRequestDelivery {
  id: string;
  tracking_url: string;
  /** Carrier or contents, e.g. "Colissimo — 2 postes fixes". */
  label?: string;
  created_at: number;
  created_by: string;
  /** When the client was e-mailed the link; absent when nobody could be. */
  notified_at?: number;
  notified_to?: string[];
}

export interface NumberRequest extends AuditableEntity {
  client_id: string;
  kind: NumberRequestKind;
  state: NumberRequestState;
  holder_type?: NumberRequestHolderType | null;
  company_siren?: string | null;
  company_name?: string | null;
  company_address?: string | null;
  company_check?: NumberRequestCompanyCheck | null;
  phone_number: string;
  country: string;
  /** `new_number`: the digits the client would like the number to start with. */
  desired_prefix: string;
  /** @deprecated No port is filed any more, so no RIO code is collected. */
  rio_code: string;
  /** @deprecated The client's own line is never taken away any more. */
  internal_line: NumberRequestInternalLine;
  /** `port_existing`: what the client answers the transferred calls on. */
  hardware_order: NumberRequestHardwareOrder;
  notes: string;
  /** Optional blocks the client has saved. See {@link NumberRequestBlock}. */
  answered: { items: string[] };
  documents: { items: NumberRequestDocument[] };
  history: { items: NumberRequestHistoryEntry[] };
  last_reminder_at: number | null;
  /**
   * Where the shipping and "number is live" e-mails go. Empty means the
   * tenant's owners — who may not have joined yet, hence the field.
   */
  notification_email?: string | null;
  /** Parcels on their way to the client. Added from the platform back-office. */
  deliveries?: { items: NumberRequestDelivery[] } | null;
  /** When the "your number is live" e-mail went out; 0 until then. */
  activation_notified_at?: number | null;
}

export interface CreateNumberRequestData {
  kind: NumberRequestKind;
  holder_type?: NumberRequestHolderType;
  /** French company: re-checked by the server, which stores the directory's name. */
  company_siren?: string;
  /** Foreign company only: its name, as the client writes it. */
  company_name?: string;
  company_check?: "foreign";
  phone_number?: string;
  country?: string;
  desired_prefix?: string;
  rio_code?: string;
  internal_line?: NumberRequestInternalLine;
  hardware_order?: NumberRequestHardwareOrder;
  notes?: string;
  notification_email?: string;
}

/**
 * What the detail form sends back. Deliberately not `Partial<NumberRequest>`:
 * `state`, `documents` and `history` are the backend's to move, and a form that
 * could PUT them would be a way around the state machine.
 */
export interface UpdateNumberRequestData {
  kind?: NumberRequestKind;
  phone_number?: string;
  country?: string;
  desired_prefix?: string;
  rio_code?: string;
  internal_line?: NumberRequestInternalLine;
  hardware_order?: NumberRequestHardwareOrder;
  notes?: string;
  notification_email?: string;
  /**
   * The block this save came from, so an optional block saved empty still
   * counts as answered. The backend appends it to `answered`.
   */
  answered_block?: NumberRequestBlock;
}
